import { unsavedSettings } from "./drafts";

/**
 * La app instalable (PWA y APK de Android): registra el service worker, busca versiones nuevas (al volver a la app y
 * cada 30 minutos) y las aplica:
 * - sola, si se acaba de abrir o está en segundo plano y no hay nada a medio escribir;
 * - si no, con el aviso «Hay una versión nueva · Actualizar».
 * También guarda el aviso de «instalar» del navegador para el botón «Instalar la app».
 */

interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let installPrompt: InstallPrompt | null = null;
const INSTALLABLE = "anota:instalable";

/** Si el navegador deja instalarla ahora (y aún no está instalada). */
export const canInstall = () => installPrompt !== null;
export const onInstallable = (fn: () => void) => {
  addEventListener(INSTALLABLE, fn);
  return () => removeEventListener(INSTALLABLE, fn);
};
export async function install(): Promise<void> {
  const p = installPrompt;
  if (!p) return;
  installPrompt = null;
  dispatchEvent(new Event(INSTALLABLE));
  await p.prompt();
}

/** Algo a medio escribir (un comentario, los ajustes sin guardar): no se recarga la página sin preguntar. */
function hasDraft(): boolean {
  if (unsavedSettings.current) return true;
  return [...document.querySelectorAll<HTMLTextAreaElement | HTMLInputElement>("textarea, input[type=text], input[type=search]")].some((el) => el.value.trim() !== "" && el.value !== el.defaultValue);
}

export function startPwa(): void {
  addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    installPrompt = e as InstallPrompt;
    dispatchEvent(new Event(INSTALLABLE));
  });
  addEventListener("appinstalled", () => {
    installPrompt = null;
    dispatchEvent(new Event(INSTALLABLE));
  });

  if (!("serviceWorker" in navigator) || import.meta.env.DEV) return;
  // Si se acaba de abrir (nadie ha tocado nada), una versión nueva se aplica sin preguntar.
  let touched = false;
  const touch = () => (touched = true);
  addEventListener("pointerdown", touch, { once: true, capture: true });
  addEventListener("keydown", touch, { once: true, capture: true });
  // Solo se recarga si lo pedimos nosotros (la primera vez que el service worker toma el control, no).
  let reloadOnChange = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!reloadOnChange) return;
    reloadOnChange = false;
    location.reload();
  });

  void navigator.serviceWorker.register("/sw.js").then((reg) => {
    // Siempre la que espera AHORA: si llegó otra versión después del aviso, la de entonces ya no existe y no contestaría.
    const apply = (reload: boolean) => {
      const worker = reg.waiting;
      if (!worker) {
        if (reload) location.reload();
        return;
      }
      reloadOnChange = reload;
      worker.postMessage({ type: "SKIP_WAITING" });
      // Por si el cambio no llega a avisar: la página viene de la red, así que recargar ya trae la versión nueva.
      if (reload) setTimeout(() => location.reload(), 4000);
    };
    const offer = () => {
      if (!navigator.serviceWorker.controller) return;
      const quiet = (!touched || document.visibilityState === "hidden") && !hasDraft();
      if (quiet) return apply(true);
      showBanner(() => apply(true));
    };
    // Una que ya esperaba al abrir: la página ya es la última (las páginas vienen de la red); se activa sin recargar.
    if (reg.waiting && navigator.serviceWorker.controller) apply(false);
    reg.addEventListener("updatefound", () => {
      const worker = reg.installing;
      worker?.addEventListener("statechange", () => {
        if (worker.state === "installed") offer();
      });
    });
    const check = () => void reg.update().catch(() => undefined);
    setInterval(check, 30 * 60_000);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") check();
      // En segundo plano, con una esperando y sin nada a medio escribir: se aplica ya.
      else if (reg.waiting && !hasDraft()) apply(true);
    });
  });
}

/** El aviso de abajo: «Hay una versión nueva de Anota · Actualizar · Luego». */
function showBanner(apply: () => void): void {
  if (document.getElementById("pwa-update")) return;
  const bar = document.createElement("div");
  bar.id = "pwa-update";
  bar.className = "pwa-update";
  bar.setAttribute("role", "status");
  const text = document.createElement("span");
  text.textContent = "Hay una versión nueva de Anota.";
  const go = document.createElement("button");
  go.type = "button";
  go.className = "btn primary";
  go.textContent = "Actualizar";
  go.addEventListener("click", () => {
    go.disabled = true;
    go.textContent = "Actualizando…";
    apply();
  });
  const later = document.createElement("button");
  later.type = "button";
  later.className = "btn";
  later.textContent = "Luego";
  later.addEventListener("click", () => bar.remove());
  bar.append(text, go, later);
  document.body.append(bar);
}
